// EventSettingsDialog - MODULE
// ***********************************************************************************************************************
import { Grid } from "@mui/material";
import * as React from "react";
import { ISessionState } from "source/models/session/sessionState";
import { IUnitDefaultHandlersNode } from "source/models/unit/unitDefaultHandlersNode";
import { IUnitModuleNode } from "source/models/unit/unitModuleNode";
import { IUnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { UserListItems } from "source/models/user/userListItems";
import { ApplicationPermissionType, DatabaseRecordType, DefaultHandlerType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as eventService from "../../../models/services/eventService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { UnitDefaultHandlersSettings } from "../../unit/Unit/UnitDefaultHandlersSettings";
import { UnitModuleSettings } from "../../unit/Unit/UnitModuleSettings";
import { EventSettings } from "./EventSettings";
import { EventUrgencyClassList } from "./EventUrgencyClassList";
import { EventTypesSettings } from "./EventTypesSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";
import { ModulePartnersSettings } from "source/components/modulePartner";

/* eslint-disable no-unused-vars */
export const enum EventsSettingsDialogMenuItem {
    Settings = "1",
    Units = "2",
    Permissions = "3",
    DefaultEventHandlersAndNotifiedPersons = "4",
    EventUrgencyClasses = "5",
    EventTypes = "6",
    EventTypeBs = "7",
    EventTypeCs = "8",
    EventPartners = "9",
    DefaultSettings = "10",
    DefaultEventUrgencyClasses = "11",
    DefaultEventTypes = "12",
}
/* eslint-enable no-unused-vars */

//EventSettingsDialog
export interface IEventSettingsDialogProp {
    sessionState: ISessionState;
    classes?: string;
    companyId: string;
    settingsActions: IPageNavMenuItem[];
    onClose: (refreshView: boolean) => void;
}

export interface IEventSettingsDialogState {
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;
    activeMenuItem: EventsSettingsDialogMenuItem;
    unitDefaultHandlersNode: IUnitDefaultHandlersNode;
}

export class EventSettingsDialog extends React.Component<IEventSettingsDialogProp, IEventSettingsDialogState> {
    private unitsRef = React.createRef<UnitModuleSettings>();
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();
    private defaultHandlersRef = React.createRef<UnitDefaultHandlersSettings>();
    private settingsRef = React.createRef<EventSettings>();
    private refreshViewOnClose = false;

    unitsPageAllowed = (props: IEventSettingsDialogProp) => !!props.settingsActions.find(i => i.id === EventsSettingsDialogMenuItem.Units);

    constructor(props: IEventSettingsDialogProp) {
        super(props);
        this.state = {
            unitModuleNode: null,
            unitNodePermissions: null,
            unitDefaultHandlersNode: null,
            activeMenuItem: this.unitsPageAllowed(props)
                ? EventsSettingsDialogMenuItem.Units
                : EventsSettingsDialogMenuItem.EventUrgencyClasses
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.unitsPageAllowed(this.props)) {
            await this.loadUnits();
        }
    }
        
    // #region Units
    onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await this.unitsRef.current.handleCancel(false);
    };

    loadUnits = async () => {
        const result = await AppUtils.callService(() => eventService.getUnits(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitModuleNode: result.unitModuleNode
        });
    };

    handleUnitModuleSettingsSave = async (obj: any) => {
        return await eventService.saveUnits(obj);
    };
    // #endregion Units

    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => eventService.getUnitNodePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await eventService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    // #region Default Handlers
    onUnitDefaultHandlersSettingsCancel = async (): Promise<boolean> => {
        return await this.defaultHandlersRef.current.handleCancel(false);
    };
    
    loadDefaultHandlers = async () => {
        const result = await AppUtils.callService(() => eventService.getUnitNodeDefaultHandlers(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitDefaultHandlersNode: result.unitNode,
        });
    };
    
    handleUnitDefaultHandlersSettingsSave = async (obj: any) => {
        const result = await eventService.saveUnitNodePermissions(obj);
        this.loadDefaultHandlers();
        return result;
    };

    handleWhistleDefaultHandlersSelectListItems = (companyId: string, unitId: string, handlerType: DefaultHandlerType, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        return eventService.defaultHandlersGetSelectUserListItems(companyId, unitId, handlerType, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
    };
    // #endregion Default Handlers

    // #region Event Settings
    onEventSettingsCancel = async (): Promise<boolean> => {
        return await this.settingsRef.current.handleCancel(false);
    };
    // #endregion Event Settings

    handleToggleActiveMenuItem = async (menuItem: EventsSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === EventsSettingsDialogMenuItem.Units) {
            if (!await this.onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === EventsSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        } else if (state.activeMenuItem === EventsSettingsDialogMenuItem.DefaultEventHandlersAndNotifiedPersons) {
            if (!await this.onUnitDefaultHandlersSettingsCancel()) return;
        } else if (state.activeMenuItem === EventsSettingsDialogMenuItem.Settings) {
            if (!await this.onEventSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === EventsSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        } else if (menuItem === EventsSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        } else if (menuItem === EventsSettingsDialogMenuItem.DefaultEventHandlersAndNotifiedPersons) {
            await this.loadDefaultHandlers();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleSubViewOk = (_closeDialog: boolean) => {
        this.refreshViewOnClose = true;
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose(this.refreshViewOnClose);
    };
    
    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="event-settings-dialog"
                maxWidth="lg"
                maxHeight="100%"
                title={Translations.Settings}
                body={
                    <GridRow flexGrow={2}>
                        <Grid item sm={3}>
                            <PageNavMenuItems
                                activeId={state.activeMenuItem}
                                items={props.settingsActions}
                                onToggleMenuItem={this.handleToggleActiveMenuItem}
                            />
                        </Grid>
                        <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.Settings &&
                                <EventSettings
                                    ref={this.settingsRef}
                                    companyId={props.companyId}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.Units &&
                                <UnitModuleSettings
                                    ref={this.unitsRef}
                                    infoText={Translations.EventModuleInfoUnits}
                                    companyId={props.companyId}
                                    sessionState={props.sessionState}
                                    applicationPermissionType={ApplicationPermissionType.Event}
                                    unitModuleNode={state.unitModuleNode}
                                    onCancel={this.handleCancel}
                                    onOk={this.handleSubViewOk}
                                    onSave={this.handleUnitModuleSettingsSave}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.Permissions &&
                                <UnitPermissionSettingsNew
                                    ref={this.permissionsRef}
                                    infoText={Translations.EventModuleInfoPermissions}
                                    companyId={props.companyId}
                                    unitNodePermissions={state.unitNodePermissions}
                                    partnerCompaniesTitle={Translations.PermissionToCreateEventsToPartners}
                                    cardView={true}
                                    onSave={this.handleUnitPermissionSettingsSave}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.DefaultEventHandlersAndNotifiedPersons &&
                                <UnitDefaultHandlersSettings
                                    ref={this.defaultHandlersRef}
                                    infoText={Translations.EventModuleInfoDefaultHandlers}
                                    companyId={props.companyId}
                                    unitNode={state.unitDefaultHandlersNode}
                                    showHandlerTypes={true}
                                    onSave={eventService.saveUnitNodeDefaultHandlers}
                                    onOk={() => this.handleSubViewOk(false)}
                                    onCancel={this.handleCancel}
                                    onGetUserSelectListItems={this.handleWhistleDefaultHandlersSelectListItems}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.EventUrgencyClasses &&
                                <EventUrgencyClassList companyId={props.companyId} />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.EventTypes &&
                                <EventTypesSettings
                                    companyId={props.companyId}
                                    category={0}
                                    onClose={() => this.handleCancel(true)}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.EventTypeBs &&
                                <EventTypesSettings
                                    companyId={props.companyId}
                                    category={1}
                                    onClose={() => this.handleCancel(true)}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.EventTypeCs &&
                                <EventTypesSettings
                                    companyId={props.companyId}
                                    category={2}
                                    onClose={() => this.handleCancel(true)}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.EventPartners &&
                                <ModulePartnersSettings
                                    databaseRecordType={DatabaseRecordType.Event}
                                    companyId={props.companyId}
                                    canAddPartner={props.sessionState.isSuperAdmin}
                                    canRemovePartner={props.sessionState.isSuperAdmin}
                                    onOk={() => this.handleSubViewOk(true)}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.DefaultSettings &&
                                <EventSettings
                                    companyId=""
                                    onOk={() => this.handleSubViewOk(false)}
                                    onCancel={() => this.handleCancel(true)}
                                />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.DefaultEventUrgencyClasses &&
                                <EventUrgencyClassList companyId="" />
                            }
                            {state.activeMenuItem === EventsSettingsDialogMenuItem.DefaultEventTypes &&
                                <EventTypesSettings
                                    companyId={null}
                                    category={0}
                                    onClose={() => this.handleCancel(true)}
                                />
                            }
                        </Grid>
                    </GridRow>
                }
                buttons={
                    state.activeMenuItem !== EventsSettingsDialogMenuItem.DefaultEventHandlersAndNotifiedPersons &&
                    state.activeMenuItem !== EventsSettingsDialogMenuItem.Settings &&
                    state.activeMenuItem !== EventsSettingsDialogMenuItem.DefaultSettings &&
                    state.activeMenuItem !== EventsSettingsDialogMenuItem.Units &&
                    state.activeMenuItem !== EventsSettingsDialogMenuItem.Permissions
                        ? [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                        : null
                }
                onClose={() => this.handleCancel(true)}/>
        );
    }
}